/**
 * Shop + Region pages: derive regional groupings from the same local-bundle API data
 * (eSIM Go + Nomad merged catalogue) so counts and prices stay consistent.
 */

import { getCountryIso } from '@/modules/countries/utils/countryIsoMap'
import { isExcludedCountryDisplayName } from '@/modules/countries/utils/excludedDisplayCountries'
import { getCountryFlag } from '@/shared/utils/countryFlags'
import type { Bundle } from '@/modules/plans/services/plans.service'
import { HOME_LOCAL_POPULAR_CURATED } from '@/modules/plans/data/localPopularDestinations'
import {
  getRegionalCountriesForDisplay,
  REGION_GROUP_ORDER,
  REGION_GROUP_LABELS,
  type RegionGroupKey,
} from '@/modules/plans/data/regionalPopularDestinations'

/** Map `/region/:name` route segment to shop region key (same buckets as Shop regional tab). */
export function decodedRegionRouteToGroupKey(decoded: string): RegionGroupKey | null {
  const n = decoded.trim().toLowerCase().replace(/\+/g, ' ')
  if (n === 'europe') return 'europe'
  if (n === 'asia') return 'asia'
  if (n === 'americas' || n === 'america') return 'americas'
  if (n === 'middle east') return 'middleEast'
  if (n === 'africa') return 'africa'
  if (n === 'oceania') return 'oceania'
  return null
}

/** API `countryObjects[0].region` values that belong to each shop region key (aligned with RegionCountries). */
export const REGION_GROUP_API_REGION_LABELS: Record<RegionGroupKey, string[]> = {
  europe: ['Europe'],
  asia: ['Asia'],
  americas: ['North America', 'South America'],
  middleEast: ['Middle East'],
  africa: ['Africa'],
  oceania: ['Oceania'],
}

/**
 * Map API/display region strings (e.g. "Europe", "North America") to the same keys used in
 * `/shop?tab=regional&region=` and REGION_GROUP_ORDER.
 */
export function regionLabelToShopGroupKey(label: string): RegionGroupKey | null {
  const trimmed = label.trim()
  if (!trimmed) return null
  const fromRoute = decodedRegionRouteToGroupKey(trimmed)
  if (fromRoute) return fromRoute
  const normalized = trimmed.toLowerCase()
  for (const key of REGION_GROUP_ORDER) {
    if (REGION_GROUP_LABELS[key].toLowerCase() === normalized) return key
    if (REGION_GROUP_API_REGION_LABELS[key].some(l => l.trim().toLowerCase() === normalized)) {
      return key
    }
  }
  return null
}

export interface RegionCountryRow {
  name: string
  countryIso: string
  flag: string
  region: string
  minPricePerGb: number
}

function apiRegionMatchesGroup(apiRegion: string, key: RegionGroupKey): boolean {
  const labels = REGION_GROUP_API_REGION_LABELS[key]
  const ar = apiRegion.trim().toLowerCase()
  return labels.some(l => l.trim().toLowerCase() === ar)
}

/**
 * Countries in a shop region that have at least one qualifying local bundle, with merged min USD/GB.
 */
export function buildRegionCountryRowsFromLocalBundles(
  localBundles: Bundle[],
  key: RegionGroupKey
): RegionCountryRow[] {
  const byIso = new Map<string, RegionCountryRow>()

  for (const bundle of localBundles) {
    if (!bundle.countryName || !bundle.countryIso) continue
    if (isExcludedCountryDisplayName(bundle.countryName)) continue

    const apiRegion = bundle.countryObjects?.[0]?.region?.trim() || 'Unknown'
    if (!apiRegionMatchesGroup(apiRegion, key)) continue

    if (bundle.unlimited) continue
    const mb = typeof bundle.dataAmount === 'number' ? bundle.dataAmount : 0
    if (mb <= 0) continue
    const gb = mb / 1000
    if (gb <= 0) continue
    const ppg = bundle.price / gb
    const iso = bundle.countryIso.trim().toUpperCase()
    const resolvedIso =
      iso.length === 2 ? iso : (getCountryIso(bundle.countryName)?.toUpperCase() ?? '')
    if (!resolvedIso || !/^[A-Z]{2}$/.test(resolvedIso)) continue
    const prev = byIso.get(resolvedIso)
    if (!prev || ppg < prev.minPricePerGb) {
      byIso.set(resolvedIso, {
        name: bundle.countryName,
        countryIso: resolvedIso,
        flag: getCountryFlag(resolvedIso),
        region: apiRegion,
        minPricePerGb: ppg,
      })
    }
  }

  return Array.from(byIso.values()).sort((a, b) => a.name.localeCompare(b.name))
}

export function countRegionCountriesFromApi(localBundles: Bundle[], key: RegionGroupKey): number {
  return buildRegionCountryRowsFromLocalBundles(localBundles, key).length
}

/** Home local tab: curated 16 countries with live min USD/GB from the same catalogue as Shop. */
export function buildLocalPopularShowcaseCardsFromApi(
  localBundles: Bundle[]
): Array<{ name: string; iso: string; flag: string; priceStr: string; available: boolean }> {
  const pricedByIso = new Map<string, number>()

  for (const bundle of localBundles) {
    if (!bundle.countryName || !bundle.countryIso) continue
    if (isExcludedCountryDisplayName(bundle.countryName)) continue
    if (bundle.unlimited) continue
    const mb = typeof bundle.dataAmount === 'number' ? bundle.dataAmount : 0
    if (mb <= 0) continue
    const gb = mb / 1000
    if (gb <= 0) continue
    const ppg = bundle.price / gb
    const iso = bundle.countryIso.trim().toUpperCase()
    const resolvedIso =
      iso.length === 2 ? iso : (getCountryIso(bundle.countryName)?.toUpperCase() ?? '')
    if (!resolvedIso || !/^[A-Z]{2}$/.test(resolvedIso)) continue
    const prev = pricedByIso.get(resolvedIso)
    if (prev == null || ppg < prev) {
      pricedByIso.set(resolvedIso, ppg)
    }
  }

  return HOME_LOCAL_POPULAR_CURATED.map(c => {
    const min = pricedByIso.get(c.iso)
    const available = min != null && !Number.isNaN(min)
    return {
      name: c.name,
      iso: c.iso,
      flag: c.flag,
      priceStr: available ? min.toFixed(2) : '0',
      available,
    }
  })
}

/** Showcase strip: static country order with live prices where a local bundle exists. */
export function buildRegionalShowcaseCardsFromApi(
  localBundles: Bundle[],
  key: RegionGroupKey
): Array<{ name: string; flag: string; countryIso: string; price: string; available: boolean }> {
  const priced = new Map<string, number>()
  for (const row of buildRegionCountryRowsFromLocalBundles(localBundles, key)) {
    priced.set(row.name.toLowerCase(), row.minPricePerGb)
    priced.set(row.countryIso.toUpperCase(), row.minPricePerGb)
  }

  return getRegionalCountriesForDisplay(key).map(c => {
    const iso = getCountryIso(c.name)?.toUpperCase() ?? ''
    const byName = priced.get(c.name.toLowerCase())
    const byIso = iso ? priced.get(iso) : undefined
    const min = byName ?? byIso
    const available = min != null && !Number.isNaN(min)
    return {
      name: c.name,
      flag: c.flag,
      countryIso: iso,
      price: available ? min.toFixed(2) : '0',
      available,
    }
  })
}

export function bundlePricePerGb(bundle: Bundle): number | null {
  if (bundle.unlimited) return null
  const mb = typeof bundle.dataAmount === 'number' ? bundle.dataAmount : 0
  if (mb <= 0) return null
  const gb = mb / 1000
  if (gb <= 0) return null
  return bundle.price / gb
}
